import getHelper from '../../../services/getHelper';

const URI = '/projects/:id';

/**
 * @desc Helper for projects.
 */
class ProjectsHelper extends getHelper(URI, {
  supportGuides: getHelper('/support-guides/:id'),
  sections: getHelper('/sections/:id', {
    milestones: getHelper('/milestones/:id'),
  }),
}) {
  /**
   * @desc Get learning outcomes of specific project from its id.
   * @param {Number} projectId Id of the project to get.
   * @return {Promise<Array<LearningOutcome>>} The promise handling the request.
   * @example myClient.helpers.learningContent.projects.getLearningOutcomes(123);
   */
  getLearningOutcomes(projectId: number) {
    const request = super.get(projectId, false);
    request.url += '/learning-outcomes';

    return request.execute();
  }

  /**
   * @desc Get courses of specific project from its id.
   * @param {Number} projectId Id of the project to get.
   * @return {Promise<Array<Course>>} The promise handling the request.
   * @example myClient.helpers.learningContent.projects.getCourses(123);
   */
  getCourses(projectId: number) {
    const request = super.get(projectId, false);
    request.url += '/courses';

    return request.execute();
  }

  /**
   * @method getMentorGuide
   * @public
   * @description Get mentor guide of specific project from its ID.
   * @param {Number} projectId The ID of the project to get.
   * @return {Promise} The promise handling the request.
   * @example api.projects.getMentorGuide(123);
   */
  getMentorGuide(projectId: number) {
    const request = super.get(projectId, false);
    request.url += '/mentor-guide';

    return request.execute();
  }

  /**
   * @method createExternalResources
   * @public
   * @param {Number} projectId The ID of the project to create.
   * @param {Object} body The request body.
   * @return {Promise} The promise handling the request.
   * @example api.projects.createExternalResources(123);
   */
  createExternalResource(projectId: number, body: object) {
    const request = super.create(body, false);
    request.setRouteParam('id', `${projectId}`);
    request.url += '/external-resources';

    return request.execute();
  }

  /**
   * @method deleteExternalResource
   * @public
   * @param {Number} projectId
   * @param {Number} id
   * @return {Promise} The promise handling the request.
   * @example api.projects.deleteExternalResource(123, 123);
   */
  deleteExternalResource(projectId: number, id: number) {
    const request = super.delete(projectId, false);
    request.url += `/external-resources/${id}`;

    return request.execute();
  }

  /**
   * @desc Get external resources of specific project from its id.
   * @param {Number} projectId
   * @return {Promise<Array<Object>>} The promise handling the request.
   * @example myClient.helpers.learningContent.projects.getExternalResources(123);
   */
  getExternalResources(projectId: number) {
    const request = super.get(projectId, false);
    request.url += '/external-resources';

    return request.execute();
  }

  /**
   * @desc Order external resources of specific project.
   * @param {Number} projectId
   * @param {Number[]} newOrder
   * @return {Promise} The promise handling the request.
   * @example myClient.helpers.learningContent.projects.orderExternalResources(123, [3, 2, 1]);
   */
  orderExternalResources(projectId: number, newOrder: number[]) {
    const request = super.partialUpdate(projectId, { sortByIds: newOrder }, false);
    request.url += '/external-resources';
    return request.execute();
  }
}

export default ProjectsHelper;
